import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDeleteFine, useGetFine, useUpdateFine } from "../api/fines-api";
import { queryClient } from "../api/queryClient";
import { useLogeMembers } from "../api/user-api";
import { Icon } from "../components/dss/Icon";
import { FadeIn, useEnterAnimation } from "../components/dss/Motion";
import { AdminHeader, Field } from "../components/dss/Primitives";
import { Skeleton } from "../components/dss/Skeleton";
import { Fine } from "../types/entities";

const AMOUNTS = [25, 50, 75];
const STATUS_OPTIONS: { key: Fine["status"]; label: string }[] = [
  { key: "unpaid", label: "Ubetalt" },
  { key: "paid", label: "Betalt" },
];

const EditFine = () => {
  const { fineId } = useParams();
  const { data: fine, isLoading } = useGetFine(fineId);
  const { data: members } = useLogeMembers();
  const { mutateAsync: updateFine, isLoading: isSaving } = useUpdateFine();
  const { mutateAsync: deleteFine } = useDeleteFine();
  const navigate = useNavigate();

  const [amount, setAmount] = useState(25);
  const [reason, setReason] = useState("");
  const [status, setStatus] = useState<Fine["status"]>("unpaid");
  const [showDeleteConfirmation, setShowDelete] = useState(false);

  useEffect(() => {
    if (fine) {
      setAmount(fine.amount_kr);
      setReason(fine.reason);
      setStatus(fine.status);
    }
  }, [fine]);

  const payer = members?.find((m) => m.id === fine?.loge_member_id);
  const issuer = members?.find((m) => m.id === fine?.issued_by);

  const onSave = async () => {
    if (!fine) return;
    await updateFine({
      fineId: fine.id,
      updates: {
        amount_kr: amount,
        reason,
        status,
        paid_at: status === "paid" ? (fine.paid_at ?? new Date().toISOString()) : null,
      },
    });
    queryClient.invalidateQueries(["fines"]);
    navigate("/penalties");
  };

  const onDelete = async () => {
    if (!fine) return;
    await deleteFine(fine.id);
    queryClient.invalidateQueries(["fines"]);
    navigate("/penalties");
  };

  const loading = !fine || isLoading;
  const animateIn = useEnterAnimation(loading);

  return (
    <div className="mx-auto max-w-md bg-paper pt-safe min-h-app">
      <AdminHeader
        back="Bøder"
        onBack={() => navigate("/penalties")}
        title={<>Redigér<br />bøde</>}
        sub={loading ? undefined : payer?.name?.toUpperCase()}
      />

      {loading ? (
        <div className="px-7 pt-6 flex flex-col gap-5 pb-8">
          <div>
            <Skeleton className="w-16 h-3 mb-2.5" />
            <Skeleton className="w-32 h-[52px]" />
            <div className="flex gap-1.5 mt-3">
              <Skeleton className="flex-1 h-10 rounded-lg" />
              <Skeleton className="flex-1 h-10 rounded-lg" />
              <Skeleton className="flex-1 h-10 rounded-lg" />
            </div>
          </div>
          <div>
            <Skeleton className="w-14 h-3 mb-2.5" />
            <Skeleton className="w-full h-[46px] rounded-lg" />
          </div>
          <div>
            <Skeleton className="w-14 h-3 mb-2.5" />
            <Skeleton className="w-full h-[68px] rounded-lg" />
          </div>
          <div>
            <Skeleton className="w-14 h-3 mb-2.5" />
            <div className="flex gap-1.5">
              <Skeleton className="flex-1 h-10 rounded-lg" />
              <Skeleton className="flex-1 h-10 rounded-lg" />
            </div>
          </div>
          <Skeleton className="w-full h-[52px] rounded-lg" />
        </div>
      ) : (
        <>
          {issuer && (
            <div className="px-7 pt-2.5 font-mono text-[9.5px] tracking-wide text-ink3 uppercase flex items-center gap-2">
              <Icon name="coins" size={11} className="text-clay" />
              <span>Udstedt af {issuer.name}</span>
            </div>
          )}

          <FadeIn enabled={animateIn} className="px-7 pt-6 flex flex-col gap-5 pb-8">
            <Field label="Beløb">
              <div className="flex items-end gap-1.5 border-b-2 border-ink pb-1.5">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={amount === 0 ? "" : String(amount)}
                  onChange={(e) => setAmount(Number(e.target.value.replace(/\D/g, "")) || 0)}
                  placeholder="0"
                  className="w-28 bg-transparent font-display font-extrabold text-[52px] leading-[0.9] tracking-tight tabular-nums focus:outline-none"
                />
                <span className="font-mono text-[13px] text-ink3 pb-2 tracking-wide">KR</span>
              </div>
              <div className="flex gap-1.5 mt-3">
                {AMOUNTS.map((a) => (
                  <button
                    key={a}
                    onClick={() => setAmount(a)}
                    className={`flex-1 py-2.5 rounded-lg border font-mono text-[11px] font-semibold tracking-wide ${
                      amount === a ? "bg-ink border-ink text-paper" : "border-line-soft text-ink3"
                    }`}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Hvem">
              <div className="flex items-center gap-2.5 bg-card border border-line-soft rounded-lg py-3 px-3.5">
                <span className="text-base">{payer?.emoji}</span>
                <span className="flex-1 font-display text-[15px] font-bold uppercase tracking-tight">
                  {payer?.name}
                </span>
              </div>
            </Field>

            <Field label="Grund" hint="Vises i hovedbogen for alle medlemmer">
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                className="w-full bg-card border border-line-soft rounded-lg py-3 px-3.5 text-base text-ink2 focus:outline-none"
              />
            </Field>

            <Field label="Status">
              <div className="flex gap-1.5">
                {STATUS_OPTIONS.map((o) => (
                  <button
                    key={o.key}
                    onClick={() => setStatus(o.key)}
                    className={`flex-1 py-2.5 rounded-lg border font-mono text-[10px] tracking-wide uppercase ${
                      status === o.key
                        ? `text-paper ${o.key === "paid" ? "bg-vote-yes border-vote-yes" : "bg-clay border-clay"}`
                        : "border-line-soft text-ink3"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </Field>

            <button
              disabled={isSaving}
              onClick={onSave}
              className="w-full py-4 rounded-lg bg-vote-yes text-paper font-display text-sm font-bold tracking-wide uppercase flex items-center justify-center gap-2"
            >
              <Icon name="check" size={15} />
              Gem
            </button>

            {showDeleteConfirmation ? (
              <button
                onClick={onDelete}
                className="w-full py-3.5 rounded-lg bg-clay text-paper font-display text-[13px] font-bold uppercase tracking-wide"
              >
                Bekræft sletning
              </button>
            ) : (
              <button
                onClick={() => setShowDelete(true)}
                className="w-full py-3.5 rounded-lg border border-clay text-clay font-display text-[13px] font-bold uppercase tracking-wide flex items-center justify-center gap-2"
              >
                <Icon name="trash-can" variant="regular" size={13} />
                Slet bøde
              </button>
            )}
          </FadeIn>
        </>
      )}
    </div>
  );
};

export default EditFine;
